import React, { useState, useCallback, useEffect } from "react";
import Cropper from "react-easy-crop";
import getCroppedImg from "./utils/cropImage";
import "./Upload-Image.css";
import BottomNav from "../BottomNav";
import { onAuthStateChanged } from "firebase/auth";
import { FaArrowLeft, FaUpload } from "react-icons/fa";
import { useNavigate, useLocation } from "react-router-dom";
import { getAuth } from "firebase/auth";
import { app } from "../firebase";
import { uploadPostImage } from "../utils/imageUpload";
import { notificationService } from '../utils/notificationService';

const UploadImage = () => {
  const [imageSrc, setImageSrc] = useState(null);
  const [croppedImage, setCroppedImage] = useState(null);
  const [crop, setCrop] = useState({ x: 0, y: 0 });
  const [zoom, setZoom] = useState(1);
  const [croppedAreaPixels, setCroppedAreaPixels] = useState(null);
  const [showCropper, setShowCropper] = useState(false);
  const [showConfirmPopup, setShowConfirmPopup] = useState(false);
  const [saving, setSaving] = useState(false);
  const { state: textState } = useLocation();
  const [title] = useState(textState?.title || "");
  const [tags] = useState(textState?.tags || []);
  const [description] = useState(textState?.description || "");
  const [uid, setUid] = useState(null);
  const [usingFullImage, setUsingFullImage] = useState(false);

  const navigate = useNavigate();
  const location = useLocation();
  const imageUrl = location.state?.imageUrl;
  const auth = getAuth(app);
  const user = auth.currentUser;

  const onCropComplete = useCallback((_, croppedPixels) => {
    setCroppedAreaPixels(croppedPixels);
  }, []);

  useEffect(() => {
    if (imageUrl) {
      setCroppedImage(imageUrl);
    }
  }, [imageUrl]);

  useEffect(() => {
    const auth = getAuth();
    const unsub = onAuthStateChanged(auth, (user) =>
      setUid(user ? user.uid : null)
    );
    return unsub;
  }, []);

  const handleImageChange = async (e) => {
    const file = e.target.files[0];
    if (file) {
      const imageDataUrl = await readFile(file);
      setImageSrc(imageDataUrl);
      setShowCropper(true);
    }
  };

  const showCroppedImage = async () => {
    try {
      const cropped = await getCroppedImg(imageSrc, croppedAreaPixels);
      setCroppedImage(cropped);
      setUsingFullImage(false);

      setShowCropper(false);
      setShowConfirmPopup(true);
    } catch (e) {
      console.error("Cropping failed", e);
    }
  };

  const saveImage = async () => {
      if (!croppedImage) {
        notificationService.showWarning("Please crop or select an image first");
        return;
      }
      if (saving) return;
      setSaving(true);
      try {
        if (!uid) throw new Error("User not authenticated");

        // Resizes in the browser, uploads main image + thumbnail
        const { filePath } = await uploadPostImage(croppedImage, { uid, folder: "users" });

        navigate("/PreviewPage", {
          state: {
            localPreview: croppedImage,
            filePath,
            title,
            tags,
            description,
          }
        });
      } catch (err) {
        console.error("Image upload failed", err);
        notificationService.showError(err.message || "Upload failed");
      } finally {
        setSaving(false);
      }
    };

  return (
    <div className="postupload-page">
      <header className="postupload-header">
        <button
          type="button"
          className="postupload-back-btn"
          aria-label="Go back"
          onClick={() => navigate(-1)}
        >
          <FaArrowLeft />
        </button>
        <h1 className="postupload-page-title">Select Image</h1>
      </header>

      {showCropper && (
        <>
          <div className="postupload-crop-mat">
            <div className="postupload-crop-container">
              <Cropper
                image={imageSrc}
                crop={crop}
                zoom={zoom}
                aspect={4 / 3}
                onCropChange={setCrop}
                onZoomChange={setZoom}
                onCropComplete={onCropComplete}
              />
            </div>
          </div>
          <div className="postupload-crop-buttons">
            <button className="postupload-crop-btn" onClick={showCroppedImage}>
              Done Cropping
            </button>
            <button
              className="postupload-full-btn"
              onClick={() => {
                setCroppedImage(imageSrc);
                setUsingFullImage(true);
                setShowCropper(false);
                setShowConfirmPopup(true);
              }}
            >
              Use Full Image
            </button>
          </div>
        </>
      )}

      {!showCropper && (
        <>
          <div className="postupload-photo-slot">
            <label className="sr-only" htmlFor="post-image-input">Upload an image for your post</label>
            <input
              id="post-image-input"
              type="file"
              className="postupload-file-input"
              accept="image/*"
              onChange={handleImageChange}
            />

            {croppedImage ? (
              <div
                className="postupload-preview-bg"
                style={{ backgroundImage: `url(${croppedImage})` }}
              />
            ) : imageUrl ? (
              <div
                className="postupload-preview-bg"
                style={{ backgroundImage: `url(${imageUrl})` }}
              />
            ) : (
              <div className="postupload-placeholder">
                <FaUpload size={36} />
                <p>Tap to add a photo</p>
              </div>
            )}
          </div>

          <button
            className="postupload-continue-btn"
            onClick={saveImage}
            disabled={saving}
          >
            {saving ? "Uploading…" : "Continue"}
          </button>
        </>
      )}

      {showConfirmPopup && (
        <div className="postupload-popup-overlay">
          <div className="postupload-note-popup">
            <p>Use this photo for your post?</p>
            <div className="postupload-popup-buttons">
              <button className="postupload-popup-ok" onClick={saveImage} disabled={saving}>
                {saving ? "Uploading…" : "OK"}
              </button>
              <button className="postupload-popup-cancel" onClick={() => setShowConfirmPopup(false)} disabled={saving}>
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}

      {saving && (
        <div className="postupload-popup-overlay">
          <div className="postupload-note-popup">
            <p>Uploading your image…</p>
            <div className="postupload-spinner" aria-hidden="true"></div>
          </div>
        </div>
      )}

      <BottomNav />
    </div>
  );
};

function readFile(file) {
  return new Promise((resolve) => {
    const reader = new FileReader();
    reader.addEventListener("load", () => resolve(reader.result), false);
    reader.readAsDataURL(file);
  });
}

export default UploadImage;